import { DifferenceModule } from './types';
import { buildDifferenceUnits, compareIndexedDifferencesByReadingOrder, compareUnits, } from './sorting';
// ---------------------------------------------------------------------------
// Default module order
// Mirrors the UI tab order; Markup inserted as its own module near Graphics.
// Exported from here so both apps can import it without a separate constants file.
// ---------------------------------------------------------------------------
export const DEFAULT_MODULE_ORDER = [
    DifferenceModule.text,
    DifferenceModule.spelling,
    DifferenceModule.graphics,
    DifferenceModule.markup,
    DifferenceModule.barcode,
    DifferenceModule.braille,
    DifferenceModule.fontMetrics,
    DifferenceModule.color,
];
// ---------------------------------------------------------------------------
// computeSubgroupIndices
// Groups differences and assigns subgroupIndex from reading-order member lists.
// Depends only on the difference set — not on sort options.
// ---------------------------------------------------------------------------
export function computeSubgroupIndices(items, toNormalized, options) {
    const groupDifferences = options?.groupDifferences !== false;
    // Normalize all items and track their input positions for reading-order tiebreaks.
    const normalizedItems = items.map((item, inputIndex) => ({
        item,
        normalized: toNormalized(item),
        inputIndex,
    }));
    // Partition by module.
    const modulePartitions = new Map();
    for (const { normalized, inputIndex } of normalizedItems) {
        const bucket = modulePartitions.get(normalized.module) ?? [];
        bucket.push({ difference: normalized, inputIndex });
        modulePartitions.set(normalized.module, bucket);
    }
    // Build groups per module.
    const moduleUnits = new Map();
    for (const [module, indexedInModule] of modulePartitions) {
        const rawUnits = buildDifferenceUnits(indexedInModule, groupDifferences);
        const groups = rawUnits.map((unit) => {
            // Sort members in reading order to establish subgroupIndex 1, 2, 3, …
            const sortedMembers = [...unit.members].sort(compareIndexedDifferencesByReadingOrder);
            const isGrouped = sortedMembers.length > 1;
            const members = sortedMembers.map((indexed, i) => ({
                item: normalizedItems[indexed.inputIndex].item,
                indexed,
                subgroupIndex: isGrouped ? i + 1 : undefined,
            }));
            // Reading-order sort puts the representative first: representative === members[0].
            return { members, representative: members[0] };
        });
        moduleUnits.set(module, groups);
    }
    return { moduleUnits };
}
// ---------------------------------------------------------------------------
// computeGroupIndices
// Sorts representatives, assigns groupIndex (per module) and global index,
// and emits the caller's objects annotated with numbering in display order.
// Depends on sort options — re-run when sort/filter changes.
// ---------------------------------------------------------------------------
export function computeGroupIndices(grouped, options) {
    const moduleOrder = options?.moduleOrder ?? DEFAULT_MODULE_ORDER;
    const result = [];
    let globalIndex = 1;
    for (const module of moduleOrder) {
        const groups = grouped.moduleUnits.get(module);
        if (!groups || groups.length === 0)
            continue;
        // Bridge DifferenceGroup → DifferenceUnit so compareUnits (from sorting.ts) can sort them.
        const toDiffUnit = (g) => ({
            members: g.members.map((m) => m.indexed),
            representative: g.representative.indexed,
        });
        const sortedGroups = [...groups].sort((a, b) => compareUnits(toDiffUnit(a), toDiffUnit(b), module, options ?? {}));
        for (let groupIdx = 0; groupIdx < sortedGroups.length; groupIdx++) {
            const group = sortedGroups[groupIdx];
            const groupIndex = groupIdx + 1;
            for (const member of group.members) {
                const annotated = {
                    ...member.item,
                    index: globalIndex++,
                    groupIndex,
                    ...(member.subgroupIndex !== undefined ? { subgroupIndex: member.subgroupIndex } : {}),
                };
                result.push(annotated);
            }
        }
    }
    for (const module of grouped.moduleUnits.keys()) {
        if (!moduleOrder.includes(module) && (grouped.moduleUnits.get(module)?.length ?? 0) > 0) {
            // Not an error — textMatch is intentionally excluded — but if a genuinely new module
            // appears here, DEFAULT_MODULE_ORDER needs updating.
            console.warn(`[shared-utils] computeGroupIndices: module "${module}" is not in moduleOrder — its differences are excluded. Update DEFAULT_MODULE_ORDER if this is a new module.`);
        }
    }
    return result;
}
// ---------------------------------------------------------------------------
// computeDifferenceNumbering — convenience wrapper (both steps in one call).
// ---------------------------------------------------------------------------
export function computeDifferenceNumbering(items, toNormalized, options) {
    const grouped = computeSubgroupIndices(items, toNormalized, options);
    return computeGroupIndices(grouped, options);
}
export function computeStepAndRepeatNumbering(items, toNormalized, options) {
    const normalizedItems = items.map((item, inputIndex) => ({
        item,
        normalized: toNormalized(item),
        inputIndex,
    }));
    // Partition into repeats by (source page, matchIndex). Differences with no matchIndex are
    // excluded, mirroring the UI's getRepeatsPerPage which only buckets diffs whose matchIndex is set.
    const repeatsByKey = new Map();
    for (const { normalized, inputIndex } of normalizedItems) {
        const { matchIndex } = normalized;
        if (matchIndex === undefined)
            continue;
        const page = normalized.location?.pageNumber ?? 0;
        const key = `${page}::${matchIndex}`;
        const repeat = repeatsByKey.get(key) ?? { page, matchIndex, members: [] };
        repeat.members.push({ difference: normalized, inputIndex });
        repeatsByKey.set(key, repeat);
    }
    // Repeats stay in fixed (page asc, matchIndex asc) order regardless of the active sort.
    const orderedRepeats = [...repeatsByKey.values()].sort((a, b) => a.page - b.page || a.matchIndex - b.matchIndex);
    const result = [];
    let sequential = 1;
    for (const repeat of orderedRepeats) {
        // No group collapsing (groupDifferences=false), so every difference is its own singleton unit.
        const units = buildDifferenceUnits(repeat.members, false);
        const sortedUnits = [...units].sort((a, b) => compareUnits(a, b, DifferenceModule.graphics, options ?? {}));
        for (const unit of sortedUnits) {
            const { inputIndex } = unit.representative;
            result.push({
                ...normalizedItems[inputIndex].item,
                index: sequential,
                groupIndex: sequential,
            });
            sequential++;
        }
    }
    return result;
}
//# sourceMappingURL=numbering.js.map